import axios from 'axios';
import { constants } from '../../Helpers/constantsFile';

/**
 * Staff.
 *
 * The phone keeps this behind drawer > Users and shows it to managers only.
 * The API agrees: listing, creating and deleting a user are all
 * `restrictTo('manager')`, so a cashier who reached this screen would get a 403
 * rather than a list.
 */

const auth = (token) => ({ headers: { Authorization: token } });

/** Blocked users are filtered out by the server, so this is the live staff. */
export async function fetchUsers({ businessId, token }) {
  const { data } = await axios.get(
    `${constants.baseUrl}/users/get-business-users/${businessId}`,
    auth(token)
  );
  return data?.data?.users || [];
}

/**
 * `passwordConfirm` is required by the model's validator, and is the same
 * string as `password` — the phone sends it that way and there is no second
 * field to disagree with.
 */
export async function createUser({ payload, token }) {
  const { data } = await axios.post(`${constants.baseUrl}/users`, payload, auth(token));
  return data?.data?.user;
}

/**
 * Name, username and password only.
 *
 * `updateUser` on the server assigns a fixed set of fields and `role` is not
 * among them, so a role sent here is silently ignored. The dialog shows the
 * role but does not offer to change it, rather than offering a control that
 * quietly does nothing.
 */
export async function updateUser({ id, payload, token }) {
  const { data } = await axios.patch(`${constants.baseUrl}/users/${id}`, payload, auth(token));
  return data?.data?.user;
}

/**
 * A soft delete: the server sets `status: 'blocked'`, which drops the user from
 * this list and refuses their next sign-in. 204, no body.
 */
export async function deleteUser({ id, token }) {
  await axios.delete(`${constants.baseUrl}/users/${id}`, auth(token));
}

/* ── roles ──────────────────────────────────────────────────────────────── */

/**
 * The roles this screen can hand out — the phone's three.
 *
 * The server's enum is wider (it also knows `accountant` and the superadmin),
 * but the phone offers waiter, cashier and manager, and a role nobody can
 * explain is a role nobody should be able to assign by accident.
 */
export const ASSIGNABLE_ROLES = ['waiter', 'cashier', 'manager'];

const LABELS = {
  waiter: 'Waiter',
  cashier: 'Cashier',
  manager: 'Manager',
  accountant: 'Accountant',
  'superadmin-superadmin': 'Superadmin',
};

/** Whatever the server sent, made presentable — including roles not listed. */
export function roleLabel(role) {
  if (!role) return 'Unknown';
  return LABELS[role] || role.charAt(0).toUpperCase() + role.slice(1);
}

/** Initials, the way the phone draws a staff avatar: up to the first two words. */
export function initialsOf(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('');
}
